/**
 * OrgObjectiveCard Component
 *
 * Displays all organization-level objectives with scores and progress
 */

import React from 'react';
import { Box, Card, CardContent, Typography, IconButton, Tooltip, LinearProgress, Chip, Stack } from '@mui/material';
import { Business, Edit, DeleteOutline, Groups, Lock, LockOpen } from '@mui/icons-material';
import { useOKRDashboardContext } from '../OKRDashboardContext';
import { getScoreColor, getScoreLabel } from '../utils/scoreUtils';
import EmptyState from '../shared/EmptyState';
import { okrApi } from '../../../services/okrApi';

interface SingleOrgObjectiveProps {
    objective: any;
    isDarkMode: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canLock: boolean;
    isLocked: boolean;
    onEdit: (objective: any) => void;
    onDelete: (objective: any) => void;
    onToggleLock: (objective: any) => void;
}

const SingleOrgObjective: React.FC<SingleOrgObjectiveProps> = ({
    objective,
    isDarkMode,
    canEdit,
    canDelete,
    canLock,
    isLocked,
    onEdit,
    onDelete,
    onToggleLock,
}) => {
    const score = objective?.score || 0;
    const scoreColor = getScoreColor(score);
    const scoreLabel = getScoreLabel(score);
    const teamObjectives = objective.teamObjectives || [];
    const isGoalLocked = objective.is_locked === 1;

    return (
        <Card
            sx={{
                bgcolor: isDarkMode ? 'rgba(255,255,255,0.02)' : '#fff',
                borderRadius: 4,
                boxShadow: isDarkMode
                    ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
                    : '0 25px 50px -12px rgba(0, 0, 0, 0.1)',
                border: '1px solid',
                borderColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                overflow: 'hidden',
                position: 'relative',
            }}
        >
            {/* Gradient accent bar */}
            <Box
                sx={{
                    height: 5,
                    background: `linear-gradient(90deg, ${scoreColor} 0%, ${scoreColor}60 100%)`,
                }}
            />

            <CardContent sx={{ p: 5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3.5, flex: 1 }}>
                        {/* Icon */}
                        <Box
                            sx={{
                                width: 72,
                                height: 72,
                                borderRadius: 3,
                                background: `linear-gradient(135deg, ${scoreColor}15 0%, ${scoreColor}30 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                border: `2px solid ${scoreColor}30`,
                            }}
                        >
                            <Business
                                sx={{
                                    color: scoreColor,
                                    fontSize: 36,
                                }}
                            />
                        </Box>

                        {/* Title & Description */}
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography
                                    variant="overline"
                                    sx={{
                                        color: scoreColor,
                                        fontWeight: 800,
                                        letterSpacing: '0.1em',
                                        fontSize: '0.7rem',
                                    }}
                                >
                                    ORGANIZATION OBJECTIVE
                                </Typography>
                                {isGoalLocked && (
                                    <Chip
                                        icon={<Lock sx={{ fontSize: 12 }} />}
                                        label="LOCKED"
                                        size="small"
                                        sx={{
                                            height: 20,
                                            fontSize: '0.65rem',
                                            fontWeight: 700,
                                            bgcolor: isDarkMode ? 'rgba(251, 146, 60, 0.15)' : 'rgba(251, 146, 60, 0.1)',
                                            color: '#fb923c',
                                            border: '1px solid rgba(251, 146, 60, 0.3)',
                                            '& .MuiChip-icon': { color: '#fb923c' },
                                        }}
                                    />
                                )}
                            </Box>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 800,
                                    fontSize: '1.5rem',
                                    color: isDarkMode ? '#f1f5f9' : '#0f172a',
                                    letterSpacing: '-0.02em',
                                    lineHeight: 1.3,
                                    mb: 1.5,
                                }}
                            >
                                {objective.title}
                            </Typography>
                            {objective.description && (
                                <Typography
                                    sx={{
                                        color: isDarkMode ? '#94a3b8' : '#64748b',
                                        fontSize: '0.95rem',
                                        lineHeight: 1.6,
                                    }}
                                >
                                    {objective.description}
                                </Typography>
                            )}

                            {/* Assigned Teams */}
                            {teamObjectives.length > 0 && (
                                <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
                                    <Groups sx={{ color: isDarkMode ? '#64748b' : '#94a3b8', fontSize: 18, mt: 0.25 }} />
                                    {teamObjectives.map((to: any) => (
                                        <Chip
                                            key={to.id}
                                            label={to.dept_name || 'Team'}
                                            size="small"
                                            sx={{
                                                bgcolor: isDarkMode ? '#334155' : '#e2e8f0',
                                                color: isDarkMode ? '#e2e8f0' : '#475569',
                                                fontWeight: 600,
                                                fontSize: '0.75rem',
                                            }}
                                        />
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    </Box>

                    {/* Score Badge */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                        <Box
                            sx={{
                                bgcolor: scoreColor,
                                color: '#fff',
                                px: 3,
                                py: 1.5,
                                borderRadius: 3,
                                fontWeight: 900,
                                fontSize: '1.5rem',
                                boxShadow: `0 12px 32px ${scoreColor}50`,
                            }}
                        >
                            {Math.round(score)}%
                        </Box>
                        <Typography
                            sx={{
                                color: scoreColor,
                                fontWeight: 700,
                                fontSize: '0.8rem',
                            }}
                        >
                            {scoreLabel}
                        </Typography>

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                            {/* Lock/Unlock — admin can toggle regardless of period state */}
                            {canLock && (
                                <Tooltip title={isGoalLocked ? 'Unlock Goal — allow adding new objectives' : 'Lock Goal — prevent structural changes'}>
                                    <IconButton
                                        size="small"
                                        onClick={() => onToggleLock(objective)}
                                        sx={{
                                            bgcolor: isGoalLocked
                                                ? (isDarkMode ? 'rgba(251, 146, 60, 0.15)' : 'rgba(251, 146, 60, 0.1)')
                                                : (isDarkMode ? '#334155' : '#f1f5f9'),
                                            color: isGoalLocked ? '#fb923c' : undefined,
                                            '&:hover': {
                                                bgcolor: isGoalLocked
                                                    ? 'rgba(251, 146, 60, 0.25)'
                                                    : (isDarkMode ? '#475569' : '#e2e8f0'),
                                            },
                                        }}
                                    >
                                        {isGoalLocked ? <Lock sx={{ fontSize: 18 }} /> : <LockOpen sx={{ fontSize: 18 }} />}
                                    </IconButton>
                                </Tooltip>
                            )}
                            {/* Edit — hidden when period closed OR goal locked */}
                            {canEdit && !isLocked && !isGoalLocked && (
                                <Tooltip title="Edit Objective">
                                    <IconButton
                                        size="small"
                                        onClick={() => onEdit(objective)}
                                        sx={{
                                            bgcolor: isDarkMode ? '#334155' : '#f1f5f9',
                                            '&:hover': {
                                                bgcolor: isDarkMode ? '#475569' : '#e2e8f0',
                                            },
                                        }}
                                    >
                                        <Edit sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {/* Delete — hidden when period closed OR goal locked */}
                            {canDelete && !isLocked && !isGoalLocked && (
                                <Tooltip title="Delete Objective">
                                    <IconButton
                                        size="small"
                                        onClick={() => onDelete(objective)}
                                        sx={{
                                            bgcolor: isDarkMode ? '#334155' : '#f1f5f9',
                                            color: '#ef4444',
                                            '&:hover': {
                                                bgcolor: 'rgba(239, 68, 68, 0.15)',
                                            },
                                        }}
                                    >
                                        <DeleteOutline sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Progress Bar */}
                <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: isDarkMode ? '#94a3b8' : '#64748b',
                                fontWeight: 600,
                            }}
                        >
                            Overall Progress
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: scoreColor,
                                fontWeight: 700,
                            }}
                        >
                            {Math.round(score)}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(score, 100)}
                        sx={{
                            height: 10,
                            borderRadius: 5,
                            bgcolor: isDarkMode ? '#334155' : '#e2e8f0',
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 5,
                                background: `linear-gradient(90deg, ${scoreColor} 0%, ${scoreColor}bb 100%)`,
                                boxShadow: `0 2px 8px ${scoreColor}40`,
                            },
                        }}
                    />
                </Box>
            </CardContent>
        </Card>
    );
};

export const OrgObjectiveCard: React.FC = () => {
    const { isDarkMode, data, permissions, dialogs } = useOKRDashboardContext();
    const { orgObjectives, isLocked, setSuccess, setError, refetchData } = data;

    const handleToggleLock = async (objective: any) => {
        try {
            const result = await okrApi.toggleOrgObjectiveLock(objective.id);
            setSuccess(result.message || `Goal "${objective.title}" ${result.is_locked ? 'locked' : 'unlocked'}`);
            refetchData();
        } catch (err: any) {
            setError(err.message || 'Failed to toggle goal lock');
        }
    };

    const handleDeleteObjective = async (objective: any) => {
        const confirmed = window.confirm(
            `Delete "${objective.title}" and all its team objectives, individual objectives, and milestones? This cannot be undone.`
        );
        if (!confirmed) return;

        try {
            await okrApi.deleteObjective(objective.id, 'org');
            setSuccess(`Org objective "${objective.title}" deleted successfully`);
            refetchData();
        } catch (err: any) {
            setError(err.message || 'Failed to delete objective');
        }
    };

    if (orgObjectives.length === 0) {
        return (
            <Card
                sx={{
                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.02)' : '#fff',
                    borderRadius: 4,
                    boxShadow: isDarkMode
                        ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
                        : '0 25px 50px -12px rgba(0, 0, 0, 0.1)',
                    border: '1px solid',
                    borderColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                }}
            >
                <CardContent>
                    <EmptyState
                        title="No Organization Objectives"
                        description="Create organization objectives to get started with OKRs"
                        isDarkMode={isDarkMode}
                    />
                </CardContent>
            </Card>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {orgObjectives.map((objective) => (
                <SingleOrgObjective
                    key={objective.id}
                    objective={objective}
                    isDarkMode={isDarkMode}
                    canEdit={permissions.canEditOrgOKR}
                    canDelete={permissions.canDeleteObjectives}
                    canLock={permissions.canLockOrgOKR}
                    isLocked={isLocked}
                    onEdit={(obj) => dialogs.openEditOrgDialog(obj)}
                    onDelete={handleDeleteObjective}
                    onToggleLock={handleToggleLock}
                />
            ))}
        </Box>
    );
};

export default OrgObjectiveCard;
